import React, { useCallback, useEffect, useState } from "react";
import "./ElectionPeriod.css";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production"
        ? "https://api.soaltriskelions.com"
        : "http://localhost:5000");

export default function Voters({ currentMember, currentUser }) {
    // ================================
    // STATES
    // ================================
    const [election, setElection] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [status, setStatus] = useState(null);
    const [countdown, setCountdown] = useState(null);

    const [form, setForm] = useState({
        primaryID: "",
        firstName: "",
        lastName: "",
        mobile: "",
        email: "",
        candidate_id: "",
    });

    const [suggestions, setSuggestions] = useState([]);
    const [verificationSent, setVerificationSent] = useState(false);
    const [isCodeValid, setIsCodeValid] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [userInputCode, setUserInputCode] = useState("");
    const [voteSubmitted, setVoteSubmitted] = useState(false);
    const [voteReceipt, setVoteReceipt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingElection, setLoadingElection] = useState(true);
    const [checkingVoteStatus, setCheckingVoteStatus] = useState(true);
    const [progress, setProgress] = useState(100);
    const [fiveMinWarned, setFiveMinWarned] = useState(false);

    const [warning, setWarning] = useState("");
    const [canShowResults, setCanShowResults] = useState(false);
    const [contactSavedFromVoting, setContactSavedFromVoting] = useState(false);

    // CLOSED ELECTION NAVIGATION
    const [closedElections, setClosedElections] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const isComelecUser = String(currentUser?.role || "").toLowerCase() === "comelec";
    const comelecVotingMessage =
        "You are assigned as a COMELEC officer for this election. To preserve election integrity, COMELEC officers may monitor and manage the election but cannot cast a vote while voting is open.";

    useEffect(() => {
        if (!currentMember) return;

        setForm((prev) => ({
            ...prev,
            primaryID: currentMember.primaryid || "",
            firstName: currentMember.firstname || "",
            lastName: currentMember.lastname || "",
            mobile: currentUser?.mobile || prev.mobile || "",
            email: currentUser?.email || prev.email || "",
        }));
    }, [currentMember, currentUser]);

    const hasSavedMobile = Boolean(currentUser?.mobile || contactSavedFromVoting);
    const hasSavedEmail = Boolean(currentUser?.email || contactSavedFromVoting);
    const contactLocked = hasSavedMobile && hasSavedEmail;
    const voterDetailsComplete =
        Boolean(form.primaryID && form.firstName && form.lastName && form.mobile && form.email) &&
        /^\d+$/.test(form.mobile) &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

    const warnContactLocked = () => {
        if (contactLocked) {
            showWarning("To update your mobile number and email address, please go to the My Profile section and edit your information there.");
        }
    };

    // Helper to show warning popup
    const showWarning = (msg) => {
        setWarning(msg);
        setTimeout(() => setWarning(""), 3000);
    };

    // ================================
    // RESULTS / TURNOUT STATES
    // ================================
    const [results, setResults] = useState([]);
    const [participation, setParticipation] = useState(null);

    // ================================
    // HELPERS
    // ================================
    const turnoutPct = (voted, total) => {
        if (!total || total === 0) return 0;
        return (voted / total) * 100;
    };

    const voteSharePct = (votes, totalVotes) => {
        if (!totalVotes || totalVotes === 0) return 0;
        return (votes / totalVotes) * 100;
    };

    const formatDateTime = (value) => {
        if (!value) return "—";
        return new Date(value).toLocaleString([], {
            month: "long",
            day: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };

    const buildReceiptFromCurrentForm = (submittedAt = new Date().toISOString()) => ({
        primaryID: form.primaryID,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        submittedAt,
        election,
    });

    const turnoutClass = (pct) => {
        if (pct >= 50) return "turnout-high";
        if (pct >= 25) return "turnout-mid";
        return "turnout-low";
    };

    const totalResultVotes = results.reduce(
        (sum, r) => sum + Number(r.votes || 0),
        0
    );

    const renderLiveVotingSummary = () => {
        const totalMembers =
            Number(participation?.PHI?.total || 0) +
            Number(participation?.SIGMA?.total || 0);
        const turnoutPercent = turnoutPct(totalResultVotes, totalMembers);

        return (
            <div className="comelec-live-voting">
                <h4>Live Voting</h4>

                <div className="comelec-live-turnout">
                    <span>Total Turnout: {totalResultVotes}/{totalMembers || 0} ({turnoutPercent.toFixed(1)}%)</span>
                </div>

                <div className="comelec-live-progress">
                    <div style={{ width: `${turnoutPercent}%` }} />
                </div>

                <div className="comelec-live-table">
                    <div className="comelec-live-row comelec-live-header">
                        <span>Name</span>
                        <span>Votes %</span>
                        <span>Votes</span>
                    </div>

                    {results.length > 0 ? (
                        results.map((candidate) => {
                            const votes = Number(candidate.votes || 0);
                            const pct = voteSharePct(votes, totalResultVotes);

                            return (
                                <div className="comelec-live-row" key={candidate.id}>
                                    <span>{candidate.name}</span>
                                    <span className={pct > 0 ? "comelec-live-pill" : ""}>
                                        {pct.toFixed(1)}%
                                    </span>
                                    <span>{votes}</span>
                                </div>
                            );
                        })
                    ) : (
                        <div className="comelec-live-empty">
                            No votes recorded yet.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // 🔁 Auto-refresh CLOSED election visibility
    useEffect(() => {
        if (status !== "closed" || !election || canShowResults) return;

        const interval = setInterval(async () => {
            try {
                const res = await fetch(
                    `${API_BASE}/api/voting/elections/closed`
                );
                const data = await res.json();

                if (!data.ok || !Array.isArray(data.rows)) return;

                const current = data.rows.find(e => e.id === election.id);

                if (current?.show_unofficial_results) {
                    setCanShowResults(true);
                    clearInterval(interval);
                }
            } catch (err) {
                console.error("Auto-refresh error:", err);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [status, election, canShowResults]);

    const goPrev = () => {
        if (currentIndex < closedElections.length - 1) {
            const idx = currentIndex + 1;
            setCurrentIndex(idx);
            setElection(closedElections[idx]);
            setCanShowResults(!!closedElections[idx].show_unofficial_results);
        }
    };

    const goNext = () => {
        if (currentIndex > 0) {
            const idx = currentIndex - 1;
            setCurrentIndex(idx);
            setElection(closedElections[idx]);
            setCanShowResults(!!closedElections[idx].show_unofficial_results);
        }
    };


    // ================================
    // LOAD RESULTS WHEN VOTING IS CLOSED
    // ================================
    useEffect(() => {
        if (status !== "closed" || !election) return;

        const loadResults = async () => {
            try {
                const [resResults, resParticipation] = await Promise.all([
                    fetch(`${API_BASE}/api/voting/results/${election.id}`),
                    fetch(`${API_BASE}/api/voting/participation/${election.id}`)
                ]);

                const resultsData = await resResults.json();
                const partData = await resParticipation.json();

                // ✅ FIX GOES HERE
                if (resultsData.ok && Array.isArray(resultsData.rows)) {
                    setResults(
                        resultsData.rows.sort(
                            (a, b) => (b.votes || 0) - (a.votes || 0)
                        )
                    );
                } else {
                    setResults([]);
                }

                if (partData.ok) {
                    setParticipation(partData.data);
                } else {
                    setParticipation(null);
                }

            } catch (err) {
                console.error("Failed to load voter results:", err);
                setResults([]);
                setParticipation(null);
            }
        };

        loadResults();
    }, [status, election]);

    // ================================
    // LIVE COMELEC VOTING SUMMARY
    // ================================
    useEffect(() => {
        if (status !== "ongoing" || !election?.id || !isComelecUser) return;

        let ignore = false;

        const loadLiveVoting = async () => {
            try {
                const [resResults, resParticipation] = await Promise.all([
                    fetch(`${API_BASE}/api/voting/results/${election.id}`),
                    fetch(`${API_BASE}/api/voting/participation/${election.id}`),
                ]);

                const resultsData = await resResults.json();
                const partData = await resParticipation.json();

                if (ignore) return;

                if (resultsData.ok && Array.isArray(resultsData.rows)) {
                    setResults(
                        resultsData.rows.sort(
                            (a, b) => Number(b.votes || 0) - Number(a.votes || 0)
                        )
                    );
                } else {
                    setResults([]);
                }

                setParticipation(partData.ok ? partData.data : null);
            } catch (err) {
                if (!ignore) {
                    console.error("Failed to load COMELEC live voting summary:", err);
                    setResults([]);
                    setParticipation(null);
                }
            }
        };

        loadLiveVoting();
        const interval = setInterval(loadLiveVoting, 60 * 60 * 1000);

        return () => {
            ignore = true;
            clearInterval(interval);
        };
    }, [status, election?.id, isComelecUser]);


    // ================================
    // LOAD ACTIVE ELECTION
    // ================================
    useEffect(() => {
        const loadElection = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/voting/election/active`);
                const data = await res.json();

                if (!data.ok) {
                    setStatus("none");
                } else {
                    setStatus(data.type);
                    setElection(data.election);
                    setCanShowResults(!!data.election.show_unofficial_results);
                    if (data.type === "ongoing") setCandidates(data.candidates || []);
                }
            } catch (err) {
                console.error("Election load error:", err);
            }

            setLoadingElection(false);
        };

        loadElection();
    }, []);

    // ================================
    // LOAD CLOSED ELECTIONS (FOR NAVIGATION) — FIXED
    // ================================
    useEffect(() => {
        if (!election || status !== "closed") return;

        const loadClosed = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/voting/elections/closed`);
                const data = await res.json();

                if (!data.ok || !Array.isArray(data.rows)) return;

                setClosedElections(
                    data.rows.sort(
                        (a, b) => new Date(b.end_time) - new Date(a.end_time)
                    )
                );

                // 🔑 IMPORTANT: sync index correctly
                const idx = data.rows.findIndex(e => e.id === election.id);
                setCurrentIndex(idx >= 0 ? idx : 0);

                console.log("🧭 Closed elections loaded:", data.rows);

            } catch (err) {
                console.error("Closed elections load error:", err);
            }
        };

        loadClosed();
    }, [election, status]);

    // ================================
    // UPCOMING COUNTDOWN
    // ================================
    useEffect(() => {
        if (status === "upcoming" && election) {
            const target = new Date(election.start_time).getTime();

            const interval = setInterval(() => {
                const now = Date.now();
                const diff = target - now;

                if (diff <= 0) {
                    clearInterval(interval);
                    setCountdown("00:00:00");

                    setTimeout(async () => {
                        const res = await fetch(`${API_BASE}/api/voting/election/active`);
                        const data = await res.json();

                        if (data.ok) {
                            setStatus(data.type);
                            setElection(data.election);
                            setCandidates(data.candidates || []);
                        }
                    }, 500);

                    return;
                }

                const hours = Math.floor(diff / 3600000);
                const minutes = Math.floor((diff % 3600000) / 60000);
                const seconds = Math.floor((diff % 60000) / 1000);

                setCountdown(
                    `${hours.toString().padStart(2, "0")}:` +
                    `${minutes.toString().padStart(2, "0")}:` +
                    `${seconds.toString().padStart(2, "0")}`
                );
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [status, election]);

    // ================================
    // ONGOING COUNTDOWN
    // ================================
    useEffect(() => {
        if (status !== "ongoing" || !election) return;

        const endTime = new Date(election.end_time).getTime();
        const startTime = new Date(election.start_time).getTime();
        const totalDuration = endTime - startTime;

        const interval = setInterval(() => {
            const now = Date.now();
            const diff = endTime - now;

            setProgress(Math.max(0, (diff / totalDuration) * 100));

            if (diff <= 5 * 60 * 1000 && !fiveMinWarned) {
                showWarning("Voting will end in 5 minutes!");
                setFiveMinWarned(true);
            }

            if (diff <= 0) {
                clearInterval(interval);
                setCountdown("00:00:00");

                setTimeout(async () => {
                    const res = await fetch(`${API_BASE}/api/voting/election/active`);
                    const data = await res.json();
                    if (data.ok) {
                        setStatus(data.type);
                        setElection(data.election);
                        setCandidates(data.candidates || []);
                    }
                }, 500);

                return;
            }

            const hours = Math.floor(diff / 3600000);
            const minutes = Math.floor((diff % 3600000) / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);

            setCountdown(
                `${hours.toString().padStart(2, "0")}:` +
                `${minutes.toString().padStart(2, "0")}:` +
                `${seconds.toString().padStart(2, "0")}`
            );
        }, 1000);

        return () => clearInterval(interval);
    }, [status, election, fiveMinWarned]);

    // ================================
    // MEMBER SEARCH
    // ================================
    const handleSearch = async (field, value) => {
        if (verificationSent || isCodeValid) return;

        setForm((prev) => ({ ...prev, [field]: value }));

        if (value.trim().length < 2) {
            setSuggestions([]);
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/api/members/search?query=${value}`);
            const data = await res.json();
            if (data.ok) setSuggestions(data.members);
        } catch (err) {
            console.error("Search error:", err);
        }
    };

    const handleSelect = (member) => {
        if (verificationSent || isCodeValid) return;

        setForm({
            ...form,
            primaryID: member.primary_id,
            firstName: member.first_name,
            lastName: member.last_name,
            mobile: "",
            email: "",
        });

        setSuggestions([]);
    };

    // ================================
    // VERIFICATION TIMER
    // ================================
    useEffect(() => {
        let timer;
        if (verificationSent && timeLeft > 0) {
            timer = setInterval(() => setTimeLeft((t) => t - 1), 1000);
        } else if (verificationSent && timeLeft === 0) {
            handleCancel();
            showWarning("Verification code expired. Request a new one.");
        }
        return () => clearInterval(timer);
    }, [verificationSent, timeLeft]);

    // ================================
    // DUPLICATE CHECK
    // ================================
    const checkDuplicate = useCallback(async () => {
        const res = await fetch(`${API_BASE}/api/voting/check-duplicate`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
            body: JSON.stringify({
                primaryID: form.primaryID,
                email: form.email,
                electionID: election?.id,
            }),
        });

        return await res.json();
    }, [form.primaryID, form.email, election?.id]);

    useEffect(() => {
        if (status !== "ongoing" || !election?.id) {
            setCheckingVoteStatus(false);
            return;
        }

        if (currentMember && !form.primaryID) {
            setCheckingVoteStatus(true);
            return;
        }

        if (!form.primaryID) {
            setCheckingVoteStatus(false);
            return;
        }

        let ignore = false;
        const loadVoteStatus = async () => {
            setCheckingVoteStatus(true);

            try {
                const duplicate = await checkDuplicate();
                if (ignore) return;

                if (duplicate?.alreadyVoted && duplicate.receipt) {
                    setVoteReceipt(duplicate.receipt);
                    setVoteSubmitted(true);
                    setVerificationSent(false);
                    setIsCodeValid(false);
                }
            } catch (err) {
                console.error("Vote status check error:", err);
            } finally {
                if (!ignore) setCheckingVoteStatus(false);
            }
        };

        loadVoteStatus();
        return () => {
            ignore = true;
        };
    }, [status, election?.id, form.primaryID, currentMember, checkDuplicate]);

    // ================================
    // SEND VERIFICATION CODE
    // ================================
    const sendVerification = async () => {
        if (loading) return;

        if (isComelecUser) {
            showWarning("COMELEC officers are not eligible to cast a vote in this election.");
            return;
        }

        setLoading(true);

        const { primaryID, firstName, lastName, mobile, email, candidate_id } = form;

        if (!primaryID || !firstName || !lastName || !mobile || !email) {
            showWarning("Please complete all required fields.");
            setLoading(false);
            return;
        }

        if (!/^\d+$/.test(mobile)) {
            showWarning("Mobile number must contain numbers only.");
            setLoading(false);
            return;
        }

        if (!candidate_id) {
            showWarning("Please select a candidate.");
            setLoading(false);
            return;
        }

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(email)) {
            showWarning("Invalid email address.");
            setLoading(false);
            return;
        }

        if (currentMember && (!currentUser?.mobile || !currentUser?.email) && !contactSavedFromVoting) {
            try {
                const contactRes = await fetch(`${API_BASE}/api/me/contact`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
                    },
                    body: JSON.stringify({ mobile, email }),
                });
                const contactData = await contactRes.json();

                if (!contactRes.ok || !contactData.ok) {
                    showWarning(contactData?.error || "Failed to save mobile and email.");
                    setLoading(false);
                    return;
                }

                setContactSavedFromVoting(true);
            } catch (err) {
                console.error("Save voter contact error:", err);
                showWarning("Failed to save mobile and email.");
                setLoading(false);
                return;
            }
        }

        const duplicate = await checkDuplicate();
        if (!duplicate.ok) {
            if (duplicate.alreadyVoted && duplicate.receipt) {
                setVoteReceipt(duplicate.receipt);
                setVoteSubmitted(true);
                setVerificationSent(false);
                setIsCodeValid(false);
            }
            showWarning(duplicate.error);
            setLoading(false);
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/api/send-verification`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    to: email,
                    name: `${firstName} ${lastName}`,
                    electionTitle: election?.title || "Election",
                }),
            });

            const data = await res.json();
            if (!data.ok) {
                showWarning(data.error);
                setLoading(false);
                return;
            }

            showWarning(`Verification code sent to ${email}`);
            setVerificationSent(true);
            setTimeLeft(900);
        } catch (err) {
            console.error("Send verification error:", err);
            showWarning("Verification sending failed.");
        }

        setLoading(false);
    };

    // ================================
    // VERIFY CODE
    // ================================
    const verifyCode = async () => {
        if (!userInputCode.trim()) return showWarning("Enter your code.");

        try {
            const res = await fetch(`${API_BASE}/api/voting/verify-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: form.email,
                    code: userInputCode,
                }),
            });

            const data = await res.json();

            if (!data.ok) return showWarning(data.error);

            showWarning("Code verified!");
            setIsCodeValid(true);

        } catch (err) {
            console.error("Verify code error:", err);
        }
    };

    // ================================
    // CANCEL VERIFICATION
    // ================================
    const handleCancel = () => {
        setVerificationSent(false);
        setIsCodeValid(false);
        setUserInputCode("");
        setTimeLeft(0);
    };

    // ================================
    // SUBMIT VOTE
    // ================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isComelecUser) {
            showWarning("COMELEC officers are not eligible to cast a vote in this election.");
            return;
        }

        if (!isCodeValid) return showWarning("Please verify your code first.");

        try {
            const res = await fetch(`${API_BASE}/api/voting/vote`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
                },
                body: JSON.stringify(form),
            });

            const data = await res.json();

            if (!data.ok) {
                if (data.alreadyVoted && data.receipt) {
                    setVoteReceipt(data.receipt);
                    setVoteSubmitted(true);
                    setVerificationSent(false);
                    setIsCodeValid(false);
                }
                return showWarning(data.error);
            }

            setVoteReceipt(data.receipt || buildReceiptFromCurrentForm());
            setVoteSubmitted(true);
            setVerificationSent(false);
            setIsCodeValid(false);

        } catch (err) {
            console.error("Vote submit error:", err);
        }
    };

    // ================================
    // UI STATES
    // ================================
    if (loadingElection) {
        return (
            <div className="voters-closed">
                <h3>⏳ Loading election...</h3>
            </div>
        );
    }

    if (voteSubmitted) {
        const receipt = voteReceipt || buildReceiptFromCurrentForm();
        const receiptElection = receipt.election || election || {};
        return (
            <div className="vote-success">
                <h3>✅ Thank you for voting!</h3>
                <p className="vote-success-message">
                    We appreciate your participation and support in this election.
                </p>

                {status === "ongoing" && countdown && (
                    <div className="vote-success-countdown">
                        <h4>⏳ Voting Ends In: <b>{countdown}</b></h4>
                        <div className="vote-progress-container">
                            <div
                                className="vote-progress-bar"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                <div className="vote-success-details">
                    <h4>Voting Details</h4>
                    <p><b>Election:</b> {receiptElection.title || election?.title || "Election"}</p>
                    <p><b>Date/Time Submitted:</b> {formatDateTime(receipt.submittedAt)}</p>
                    <p><b>Voting Start:</b> {formatDateTime(receiptElection.start_time)}</p>
                    <p><b>Voting End:</b> {formatDateTime(receiptElection.end_time)}</p>
                    <p><b>Voter:</b> {receipt.firstName} {receipt.lastName}</p>
                    <p><b>Primary ID:</b> {receipt.primaryID}</p>
                </div>

                <p className="vote-success-note">
                    Your vote has been recorded. This voting page is now closed for your account.
                </p>
            </div>
        );
    }

    if (status === "ongoing" && checkingVoteStatus && !isComelecUser) {
        return (
            <div className="voters-closed vote-status-checking">
                <h3>⏳ Checking your vote status...</h3>
                <p>Please wait while we confirm your account for this election.</p>
            </div>
        );
    }

    if (status === "ongoing" && isComelecUser) {
        return (
            <div className="voters-closed comelec-ineligible-card">
                <h3>Election Oversight Access</h3>
                <p>{comelecVotingMessage}</p>
                {countdown && (
                    <div className="vote-success-countdown">
                        <h4>Voting Ends In: <b>{countdown}</b></h4>
                        <div className="vote-progress-container">
                            <div
                                className="vote-progress-bar"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                    </div>
                )}
                {election && (
                    <div className="election-details-box">
                        <h4>Election Details</h4>
                        <p><b>Title:</b> {election.title}</p>
                        <p><b>Start:</b> {new Date(election.start_time).toLocaleString()}</p>
                        <p><b>End:</b> {new Date(election.end_time).toLocaleString()}</p>
                    </div>
                )}
                {renderLiveVotingSummary()}
            </div>
        );
    }

    if (status === "none") {
        return (
            <div className="voters-closed">
                <h3>⚠️ No Election Available</h3>
            </div>
        );
    }

    if (status === "upcoming") {
        return (
            <div className="voters-closed">
                <h3>📅 Election Not Started</h3>
                {countdown && (
                    <h2 className="countdown-outline" style={{ marginTop: "10px" }}>
                        ⏳ Starts in: <b>{countdown}</b>
                    </h2>
                )}
                <p>Please wait until voting begins.</p>

                <div className="election-details-box">
                    <h4>Election Details</h4>
                    <p><b>Title:</b> {election.title}</p>
                    <p><b>Start:</b> {new Date(election.start_time).toLocaleString()}</p>
                    <p><b>End:</b> {new Date(election.end_time).toLocaleString()}</p>
                </div>
            </div>
        );
    }
    if (status === "closed") {

        return (
            <div className="voters-closed">

                {/* ===== ELECTION NAVIGATION ===== */}
                {status === "closed" && (
                    <div className="closed-nav">
                        <button
                            onClick={goPrev}
                            disabled={currentIndex === closedElections.length - 1}
                        >
                            ⬅ Previous
                        </button>

                        <button
                            onClick={goNext}
                            disabled={currentIndex === 0}
                        >
                            Next ➡
                        </button>
                    </div>
                )}


                <h3>🛑 Voting Closed</h3>

                <div className="election-details-box">
                    <h4>Election Details</h4>
                    <p><b>Title:</b> {election.title}</p>
                    <p><b>Start:</b> {new Date(election.start_time).toLocaleString()}</p>
                    <p><b>End:</b> {new Date(election.end_time).toLocaleString()}</p>
                </div>

                {!canShowResults && (
                    <div className="results-hidden">
                        <p style={{ color: "#ffd700", marginTop: "15px" }}>
                            🔒 Unofficial results are currently hidden.
                            <br />
                            Please wait for the official announcement by the COMELEC Committee.
                        </p>
                    </div>
                )}

                {/* ================= UNOFFICIAL RESULTS ================= */}
                {canShowResults && results.length > 0 && participation && (() => {

                    const phiPct = turnoutPct(
                        participation.PHI.voted,
                        participation.PHI.total
                    );

                    const sigmaPct = turnoutPct(
                        participation.SIGMA.voted,
                        participation.SIGMA.total
                    );

                    const totalVoted =
                        participation.PHI.voted + participation.SIGMA.voted;

                    const totalMembers =
                        participation.PHI.total + participation.SIGMA.total;

                    const overallPct = turnoutPct(totalVoted, totalMembers);

                    return (
                        <div className="unofficial-results">

                            <h3 className="unofficial-title">
                                📊 Unofficial {election.title} Results
                            </h3>

                            {/* Candidate vote bars */}
                            <div className="results-block">
                                <h4>Candidate Vote Distribution</h4>

                                {results.map((r, i) => {
                                    const pct = voteSharePct(r.votes || 0, totalResultVotes);
                                    const isWinner = i === 0;

                                    return (
                                        <div
                                            key={r.id}
                                            className={`result-bar-row ${isWinner ? "winner" : ""}`}
                                        >
                                            <div className="result-bar-label">
                                                <span className="candidate-name">
                                                    <b>{i + 1}.</b> {r.name}
                                                </span>

                                                <span className="candidate-stats">
                                                    {pct.toFixed(1)}% ({r.votes} votes)
                                                </span>
                                            </div>

                                            <div className={`bar-track ${isWinner ? "winner" : ""}`}>
                                                <div
                                                    className={`bar-fill ${isWinner ? "winner" : ""}`}
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}

                            </div>

                            {/* Turnout summary */}
                            <div className="results-block">
                                <h4>Voter Turnout</h4>

                                <div className="result-bar-row">
                                    <div className="result-bar-label">
                                        Overall Turnout
                                        <span>
                                            {totalVoted}/{totalMembers} ({overallPct.toFixed(1)}%)
                                        </span>
                                    </div>

                                    <div className="bar-track">
                                        <div
                                            className="bar-fill gold"
                                            style={{ width: `${overallPct}%` }}
                                        />
                                    </div>
                                </div>

                                <p className="turnout-pill">
                                    <span className={turnoutClass(phiPct)}>
                                        PHI {participation.PHI.voted}/{participation.PHI.total}
                                    </span>{" | "}
                                    <span className={turnoutClass(sigmaPct)}>
                                        SIGMA {participation.SIGMA.voted}/{participation.SIGMA.total}
                                    </span>{" | "}
                                    <span className={turnoutClass(overallPct)}>
                                        TOTAL {totalVoted}/{totalMembers}
                                    </span>
                                </p>
                            </div>

                            {/* Disclaimer */}
                            <div className="results-disclaimer">
                                ⚠️ <b>Notice:</b> These results are <b>unofficial</b>.
                                The official results of the <b>{election.title}</b> will be
                                formally announced by the <b>COMELEC Committee</b> through
                                the official Group Chat and Facebook Page.
                            </div>

                        </div>
                    );
                })()}
            </div>
        );
    }

    // ==========================================================
    // ONGOING — VOTING FORM
    // ==========================================================
    return (
        <>
            {/* THEMED WARNING POPUP */}
            {warning && (
                <div className="warning-popup">
                    ⚠️ {warning}
                </div>
            )}

            <div className="voters-container">

                {/* TIMER */}
                {countdown && (
                    <div className="timer-wrapper">
                        <h3
                            className={
                                countdown.startsWith("00:00") ||
                                    countdown.startsWith("00:01")
                                    ? "blinking-timer"
                                    : ""
                            }
                            style={{
                                color: "#ffd700",
                                textAlign: "center",
                                fontWeight: "bold",
                                fontSize: "1.4rem",
                                marginBottom: "10px"
                            }}
                        >
                            ⏳ Voting Ends In: <b>{countdown}</b>
                        </h3>

                        <div className="vote-progress-container">
                            <div
                                className="vote-progress-bar"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                <h2 className="voting-title">{election?.title || "Election"}</h2>

                {/* ************** REAL FORM ************** */}
                <form onSubmit={handleSubmit} autoComplete="off">

                    <div className="gold-separator"></div>
                    <h3 style={{ display: "none" }}>
                        📝 Complete the form:
                    </h3>

                    <h3 style={{ color: "white", marginBottom: "10px", textAlign: "left" }}>
                        Voters Details:
                    </h3>

                    {/* SEARCHABLE FIELDS */}
                    <div className="form-group" style={{ position: "relative" }}>
                        <input
                            placeholder="Primary ID"
                            value={form.primaryID}
                            onChange={(e) => handleSearch("primaryID", e.target.value)}
                            disabled={!!currentMember || verificationSent || isCodeValid}
                            title={currentMember ? "Loaded from your login account" : ""}
                        />

                        <input
                            placeholder="First Name"
                            value={form.firstName}
                            onChange={(e) => handleSearch("firstName", e.target.value)}
                            disabled={!!currentMember || verificationSent || isCodeValid}
                            title={currentMember ? "Loaded from your login account" : ""}
                        />

                        <input
                            placeholder="Last Name"
                            value={form.lastName}
                            onChange={(e) => handleSearch("lastName", e.target.value)}
                            disabled={!!currentMember || verificationSent || isCodeValid}
                            title={currentMember ? "Loaded from your login account" : ""}
                        />

                        {/* SUGGESTIONS */}
                        {!currentMember && <ul className="suggestions-dropdown">
                            {suggestions.map((m) => (
                                <li key={m.primary_id} onClick={() => handleSelect(m)}>
                                    <b>{m.primary_id}</b> — {m.first_name} {m.last_name}
                                </li>
                            ))}
                        </ul>}
                    </div>

                    {/* MOBILE */}
                    <input
                        placeholder="Mobile Number"
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={form.mobile}
                        onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })}
                        readOnly={hasSavedMobile || verificationSent || isCodeValid}
                        onClick={warnContactLocked}
                        onFocus={warnContactLocked}
                        title={hasSavedMobile ? "Edit this from My Profile" : ""}
                    />

                    {/* EMAIL */}
                    <input
                        placeholder="Email Address"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        readOnly={hasSavedEmail || verificationSent || isCodeValid}
                        onClick={warnContactLocked}
                        onFocus={warnContactLocked}
                        title={hasSavedEmail ? "Edit this from My Profile" : ""}
                    />

                    {/* NOTE */}
                    <div className="required-note">
                        {voterDetailsComplete
                            ? "To update your mobile number and email address, please go to the My Profile section and edit your information there."
                            : "Please ensure all fields are complete and accurate before submitting your vote."}
                    </div>

                    {/* CANDIDATES */}
                    <div className="gold-separator"></div>
                    <h3>Select Your Candidate</h3>

                    <div className="voter-candidate-list">
                        {candidates.map((c) => (
                            <div
                                key={c.id}
                                className={
                                    form.candidate_id === c.id
                                        ? "voter-candidate selected"
                                        : "voter-candidate"
                                }
                                onClick={() => {
                                    if (!verificationSent && !isCodeValid) {
                                        setForm({
                                            ...form,
                                            candidate_id:
                                                form.candidate_id === c.id ? null : c.id,
                                        });
                                    }
                                }}
                            >
                                {c.name}
                            </div>
                        ))}
                    </div>

                    {/* SEND VERIFICATION */}
                    {!verificationSent && !isCodeValid && (
                        <button
                            type="button"
                            className="confirm-btn"
                            onClick={sendVerification}
                            disabled={loading}
                        >
                            {loading ? "Sending..." : "Send Verification Code"}
                        </button>
                    )}

                    {/* VERIFY SECTION */}
                    {verificationSent && !isCodeValid && (
                        <div className="verify-section">
                            <p>Enter the code sent to your email:</p>

                            <input
                                type="text"
                                value={userInputCode}
                                onChange={(e) => setUserInputCode(e.target.value)}
                            />

                            <div className="verify-actions">
                                <button type="button" onClick={verifyCode}>
                                    Verify
                                </button>
                                <button type="button" className="cancel-btn" onClick={handleCancel}>
                                    Cancel
                                </button>
                            </div>

                            <p className="timer">
                                ⏱ {Math.floor(timeLeft / 60)}:
                                {(timeLeft % 60).toString().padStart(2, "0")}
                            </p>
                        </div>
                    )}

                    {/* SUBMIT FINAL VOTE */}
                    {isCodeValid && (
                        <button type="submit" className="submit-btn">
                            Submit Vote
                        </button>
                    )}
                </form>
            </div>
        </>
    );
}
